import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { 
  Grid, 
  Paper, 
  Typography, 
  Card, 
  CardContent, 
  Tabs, 
  Tab, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Box,
  CircularProgress,
  Button,
  Divider
} from '@material-ui/core';
import { 
  makeStyles, 
  useTheme 
} from '@material-ui/styles';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import CountUp from 'react-countup';
import { 
  LocalFlorist, 
  Assignment, 
  Warning, 
  Check, 
  DateRange, 
  Person, 
  BusinessCenter,
  TrendingUp,
  Map,
  Add
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import clsx from 'clsx';
import { connect } from 'react-redux'
import ChemicalBlends from '../HerbicideBlendsList'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFlask,
  faFlaskVial,
  faBuilding
} from '@fortawesome/free-solid-svg-icons'
import JobsiteSprayLogs from '../JobsiteSprayLogs'
import DashboardHeader from './DashboardHeader'
import CreateChemicalModal from './AddNewChemicalModal';
import CreateSprayLogModal from './CreateSprayLogModal';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: 'center',
    height: '100%',
  },
  statCard: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  statIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1),
  },
  statValue: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5),
  },
  statLabel: {
    fontSize: '1rem',
  },
  tabContent: {
    marginTop: theme.spacing(2),
  },
  tableContainer: {
    maxHeight: 420,
  },
  chart: {
    height: 360,
    marginTop: theme.spacing(2),
  },
  chipSuccess: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  chipWarning: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  chipError: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  chipPrimary: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50vh',
  },
  progressText: {
    marginLeft: theme.spacing(2),
  },
  header: {
    marginBottom: theme.spacing(4),
  },
  techCard: {
    marginBottom: theme.spacing(2),
  },
  crewName: {
    fontWeight: 'bold',
  },
  priorityHigh: {
    color: theme.palette.error.main,
  },
  priorityMedium: {
    color: theme.palette.warning.main,
  },
  priorityLow: {
    color: theme.palette.info.main,
  },
  dateCell: {
    whiteSpace: 'nowrap',
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  mapPlaceholder: {
    height: 400,
    backgroundColor: '#f5f5f5',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: theme.shape.borderRadius,
  },
  chemicalChip: {
    margin: theme.spacing(0.5),
  }
}));

const SprayJobDashboard = (props) => {
  const classes = useStyles();
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddChemicalModal, setShowAddChemicalModal] = useState(false);
  const [showCreateSprayLogModal, setShowCreateSprayLogModal] = useState(false)

  console.log('176...SprayJobDashboard', props)
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${props.url}/api/hmi/genericGetRequest?type=SPRAY_DASHBOARD_DETAILS`);
      console.log('185...', response)
      setDashboardData(response.data.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load dashboard data');
      setLoading(false);
      console.error('Error fetching dashboard data:', err);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // Refresh data every 5 minutes
    // const intervalId = setInterval(fetchDashboardData, 5 * 60 * 1000);
    // return () => clearInterval(intervalId);
  }, []);

  const getPriorityClass = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return classes.priorityHigh;
      case 'medium':
        return classes.priorityMedium;
      case 'low':
        return classes.priorityLow;
      default:
        return '';
    }
  };

  const getStatusChipClass = (status) => {
    if (!status) return classes.chipWarning;
    
    switch (status.toLowerCase()) {
      case 'completed':
      case 'closed':
      case 'resolved':
        return classes.chipSuccess;
      case 'in progress':
        return classes.chipPrimary;
      case 'open':
        return classes.chipWarning;
      default:
        return classes.chipWarning;
    }
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
        <Typography variant="h6" className={classes.progressText}>
          Loading Dashboard Data...
        </Typography>
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
        <Button 
          variant="outlined" 
          color="inherit" 
          size="small" 
          onClick={fetchDashboardData}
          style={{ marginLeft: '16px' }}
        >
          Retry
        </Button>
      </Alert>
    );
  }

  if (!dashboardData) {
    return (
      <Alert severity="warning">
        No dashboard data available.
        <Button 
          variant="outlined" 
          color="inherit" 
          size="small" 
          onClick={fetchDashboardData}
          style={{ marginLeft: '16px' }}
        >
          Reload
        </Button>
      </Alert>
    );
  }

  const renderAddChemicalComponent = () => {
    const handleSaveNewChemical = async (d) => {
      console.log('save this', d)
      handleCloseModal()
      try {
        setLoading(true);
        const obj = {
          type: 'SAVE_NEW_HERBICIDE',
          data: d
        }
        const response = await axios.post(`${props.url}/api/hmi/genericPostRequest?type=SAVE_NEW_HERBICIDE`, obj);
        console.log('291 w/ new chemical...', response)
        if (response?.data?.item?._id) {
          const newChem = response.data.item
          console.log('add this guy', newChem)
          setDashboardData(prev => ({
            ...prev,
            chemicals: [
              ...prev.chemicals,
              newChem
            ]
          }))
        }
        setLoading(false);
      } catch (err) {
        setError('Failed to load dashboard data');
        setLoading(false);
        console.error('Error fetching dashboard data:', err);
      }
  
    }
    const handleCloseModal = () => {
      setShowAddChemicalModal(false)
    }
    return (
      <>
        <CreateChemicalModal 
          open={showAddChemicalModal}
          onClose={handleCloseModal}
          onSave={handleSaveNewChemical}
        />
        <Button
            variant="outlined" 
            color="primary" 
            size="small" 
            onClick={() => setShowAddChemicalModal(true)}
            style={{ }}
        
        >Add New</Button>
      </>
    )
  }
  // Create a color scale for the chart lines (one color per employee)
  const getChartColors = () => {
    if (!dashboardData?.chartData || dashboardData.chartData.length === 0) return [];
    
    // Get the employee names from the first chart data entry
    const firstEntry = dashboardData.chartData[0];
    const employeeNames = Object.keys(firstEntry).filter(key => key !== 'date');
    
    // Define a set of colors to use
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.info.main,
      '#8884d8',
      '#82ca9d',
      '#ffc658',
      '#ff7300'
    ];

    // Map each employee to a color
    return employeeNames.map((name, index) => ({
      name,
      color: colors[index % colors.length]
    }));
  };

  const chartColors = getChartColors();

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h3" gutterBottom>
              Spray Operations Dashboard
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              Last updated: {moment().format('MMMM D, YYYY [at] h:mm A')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => {
              console.log('Manual Spray Log Entry button clicked');
              setShowCreateSprayLogModal(true);
            }}
            size="large"
          >
            Manual Spray Log Entry
          </Button>
        </Box>
      </div>
      <DashboardHeader data={dashboardData} url={props.url}/>
      {/* Stats Overview */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard} style={{ backgroundColor: theme.palette.primary.main }}>
            <CardContent>
              <Assignment className={classes.statIcon} />
              <Typography variant="h4" className={classes.statValue}>
                <CountUp end={dashboardData.stats.openWorkOrders} duration={1.5} separator="," />
              </Typography>
              <Typography variant="body1" className={classes.statLabel}>
                Open Work Orders
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard} style={{ backgroundColor: theme.palette.warning.main }}>
            <CardContent>
              <Warning className={classes.statIcon} />
              <Typography variant="h4" className={classes.statValue}>
                <CountUp end={dashboardData.stats.outstandingIssues} duration={1.5} separator="," />
              </Typography>
              <Typography variant="body1" className={classes.statLabel}>
                Outstanding Issues
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard} style={{ backgroundColor: theme.palette.success.main }}>
            <CardContent>
              <Check className={classes.statIcon} />
              <Typography variant="h4" className={classes.statValue}>
                <CountUp end={dashboardData.stats.completedChecksThisWeek} duration={1.5} separator="," />
              </Typography>
              <Typography variant="body1" className={classes.statLabel}>
                Completed This Week
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard} style={{ backgroundColor: theme.palette.info.main }}>
            <CardContent>
              <DateRange className={classes.statIcon} />
              <Typography variant="h4" className={classes.statValue}>
                <CountUp end={dashboardData.stats.scheduledChecksNextWeek} duration={1.5} separator="," />
              </Typography>
              <Typography variant="body1" className={classes.statLabel}>
                Scheduled Next Week
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider className={classes.divider} />

      {/* Activity Chart */}
      <Paper className={classes.paper} style={{ marginBottom: '24px' }}>
        <Typography variant="h5" gutterBottom>
          <TrendingUp style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Spray Activity - Past 30 Days
        </Typography>
        <div className={classes.chart}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={dashboardData.chartData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              {chartColors.map(({ name, color }) => (
                <Line
                  key={name}
                  type="monotone"
                  dataKey={name}
                  stroke={color}
                  activeDot={{ r: 8 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Paper>

       
      {/* Tabs Section */}
      <Paper className={classes.paper}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Work Orders" icon={<Assignment />} />
          <Tab label="Issues" icon={<Warning />} />
          <Tab label="Spray Techs" icon={<Person />} />
          <Tab label="Crews" icon={<BusinessCenter />} />
          <Tab label="Chemicals" icon={<FontAwesomeIcon icon={faFlask} />} />
          <Tab label="Blends" icon={<FontAwesomeIcon icon={faFlaskVial} />} />
          <Tab label="Jobsites" icon={<FontAwesomeIcon icon={faBuilding} />} />
          {/* <Tab label="Map View" icon={<Map />} /> */}
        </Tabs>

        <div className={classes.tabContent}>
          {/* Work Orders Tab */}
          {tabValue === 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                Open Work Orders ({dashboardData.workorders?.length || 0})
              </Typography>
              <TableContainer className={classes.tableContainer}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Job Site</TableCell>
                      <TableCell>Service Code</TableCell>
                      <TableCell>Crew</TableCell>
                      <TableCell>Scheduled Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.workorders?.length > 0 ? (
                      dashboardData.workorders.map((wo) => (
                        <TableRow key={wo._id} hover>
                          <TableCell>{wo.jobSite?.name || 'N/A'}</TableCell>
                          <TableCell>{wo.serviceCode}</TableCell>
                          <TableCell>{wo.crew?.name || 'Unassigned'}</TableCell>
                          <TableCell className={classes.dateCell}>
                            {wo.scheduledDate ? moment(wo.scheduledDate).format('MMM D, YYYY') : 'Not Scheduled'}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={wo.workComplete ? 'Completed' : 'Open'} 
                              className={wo.workComplete ? classes.chipSuccess : classes.chipWarning}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No work orders available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Issues Tab */}
          {tabValue === 1 && (
            <>
              <Typography variant="h6" gutterBottom>
                Outstanding Issues ({dashboardData.issues?.length || 0})
              </Typography>
              <TableContainer className={classes.tableContainer}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Job Site</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Report Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.issues?.length > 0 ? (
                      dashboardData.issues.map((issue) => (
                        <TableRow key={issue._id} hover>
                          <TableCell>{issue.jobSite?.name || 'N/A'}</TableCell>
                          <TableCell>{issue.type}</TableCell>
                          <TableCell>{issue.description}</TableCell>
                          <TableCell className={getPriorityClass(issue.priority)}>
                            {issue.priority || 'Medium'}
                          </TableCell>
                          <TableCell className={classes.dateCell}>
                            {issue.reportDate ? moment(issue.reportDate).format('MMM D, YYYY') : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={issue.status || 'Open'} 
                              className={getStatusChipClass(issue.status)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No issues available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Spray Techs Tab */}
          {tabValue === 2 && (
            <Grid container spacing={3}>
              {dashboardData.sprayTechs?.length > 0 ? (
                dashboardData.sprayTechs.map((tech) => (
                  <Grid item xs={12} sm={6} md={4} key={tech._id}>
                    <Card className={classes.techCard}>
                      <CardContent>
                        <Typography variant="h6">{tech.firstName} {tech.lastName}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          Assigned to: {tech.currentCrew?.name || 'Not Assigned'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Last Location: {tech.currentCrew?.lastLocation?.toString() || 'Unknown'}
                        </Typography>
                        <Box mt={2}>
                          <Chip 
                            label="Spray Tech" 
                            className={classes.chipPrimary}
                            size="small"
                            icon={<LocalFlorist style={{ color: 'inherit' }} />}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Alert severity="info">No spray technicians available</Alert>
                </Grid>
              )}
            </Grid>
          )}

          {/* Crews Tab */}
          {tabValue === 3 && (
            <Grid container spacing={3}>
              {dashboardData.sprayCrews?.length > 0 ? (
                dashboardData.sprayCrews.map((crew) => {
                  
                  console.log('Crew details...location??', crew) 
                  
                  return (
                    crew ? (
                      <Grid item xs={12} sm={6} key={crew._id}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" className={classes.crewName}>{crew.name}</Typography>
                            <Typography variant="body2" color="textSecondary">
                              Foreman: {crew.foremanName || 'Not Assigned'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Last Location: {crew.lastLocation?.toString() || 'Unknown'}
                            </Typography>
                            
                            <Typography variant="subtitle2" style={{ marginTop: '16px', marginBottom: '8px' }}>
                              Team Members:
                            </Typography>
                            
                            <Grid container spacing={1}>
                              {dashboardData.sprayTechs
                                .filter(tech => tech.currentCrew && tech.currentCrew._id === crew._id)
                                .map(tech => (
                                  <Grid item key={tech._id}>
                                    <Chip 
                                      label={`${tech.firstName} ${tech.lastName}`} 
                                      size="small"
                                      className={classes.chipPrimary}
                                    />
                                  </Grid>
                                ))}
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    ) : null
                  )})
                ) : (
                <Grid item xs={12}>
                  <Alert severity="info">No spray crews available</Alert>
                </Grid>
              )}
            </Grid>
          )}

          {/* Chemicals Tab */}
          {tabValue === 4 && (
            <>
              <Typography variant="h6" gutterBottom>
                Available Chemicals ({dashboardData.chemicals?.length || 0})
              </Typography>
              {renderAddChemicalComponent()}
              <TableContainer className={classes.tableContainer}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Active Ingredient</TableCell>
                      <TableCell>Target Areas</TableCell>
                      <TableCell>EPA Number</TableCell>
                      <TableCell>Options</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.chemicals?.length > 0 ? (
                      dashboardData.chemicals.map((chem) => {
                        console.log('Chem...', chem)
                        return (
                        <TableRow key={chem._id} hover>
                          <TableCell>{chem.name}</TableCell>
                          <TableCell>{chem?.herbicideDetails?.activeIngredient}</TableCell>
                          <TableCell>
                            {chem?.herbicideDetails?.targetLocation?.length > 0 ? (
                              <Box display="flex" flexWrap="wrap">
                                {chem.herbicideDetails.targetLocation.map(opt => {
                                  // console.log('optTarget areaion...', opt)
                                  return (
                                  <Chip 
                                    key={opt}
                                    label={opt} 
                                    className={classes.chemicalChip}
                                    size="small"
                                  />
                                )})}
                              </Box>
                            ) : '' }
                          </TableCell>
                          <TableCell>{chem?.herbicideDetails?.epaRegistrationNumber}</TableCell>
                          {/* <TableCell>{chem.description || 'No description available'}</TableCell> */}
                          <TableCell>
                            {chem.options?.length > 0 ? (
                              <Box display="flex" flexWrap="wrap">
                                {chem.options.map(opt => {
                                  console.log('option...', opt)
                                  let optLabel = `${opt.size ? opt.size : ''}${opt.msrp ? `  $${opt.msrp}` : ''}`
                                  return (
                                  <Chip 
                                    key={opt._id}
                                    label={optLabel} 
                                    className={classes.chemicalChip}
                                    size="small"
                                  />
                                )})}
                              </Box>
                            ) : 'No options available'}
                          </TableCell>
                        </TableRow>
                      )})
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          No chemicals available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          {tabValue === 5 && (
             <ChemicalBlends 
                chemicals={dashboardData.chemicals}
             />
          )}
          {/* Sites Tab */}
          {tabValue === 6 && (
              <>
              <Typography variant="h6" gutterBottom>
                Sites with Spray Logs ({dashboardData.sitesWithSprayLogs?.length || 0})
              </Typography>

              <JobsiteSprayLogs jobsites={dashboardData.sitesWithSprayLogs.allMergedSites} url={props.url} chemicals={dashboardData.chemicals} />
              </>
          )}

            {tabValue === 7 && (
            <>
              <Typography variant="h6" gutterBottom>
                Sites Map View ({dashboardData.sitesWithSprayLogs?.length || 0})
              </Typography>

              {/* Placeholder for map visualization */}
              <div className={classes.mapPlaceholder}>
                <Typography variant="body1" color="textSecondary">
                  Map visualization of spray sites would be displayed here
                </Typography>
              </div>
            </>
          )}
        </div>
      </Paper>
      
      {/* Create Spray Log Modal */}
      <CreateSprayLogModal
        open={showCreateSprayLogModal}
        url={props.url}
        AllWorkorders={dashboardData.workorders}
        jobsites={dashboardData?.sitesWithSprayLogs?.allMergedSites || []}
        chemicals={dashboardData?.chemicals || []}
        sprayTechs={dashboardData?.sprayTechs || []}
        onClose={(refresh) => {
          setShowCreateSprayLogModal(false);
          if (refresh) {
            fetchDashboardData();
          }
        }}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  // console.log('Do we have chemicals??', state)
  return {
    authenticated: state.authenticated,
    notification: state.notification,
    url: state.url,
    entities: state.entities,
    perms: state.perms
  }
}

export default connect(mapStateToProps)(SprayJobDashboard)

// export default SprayJobDashboard;